import { useMemo } from 'react';

import LineChart, { GraphicalLineChartDataset } from 'components/LineChart/LineChart';
import { useTranslation } from 'react-i18next';
import { TimelineChartPoint } from 'types/CodegenReportTypes';

import { getOrderedColorSet, orderedColorSets } from 'utilities/colorHelpers';
import { Typography } from '@trackman/web-shared-components';

type Props = {
  label: string;
  heatLinePoints: TimelineChartPoint[];
  baseLinePoints: TimelineChartPoint[];
};

const HeatLineChart = ({ label, heatLinePoints, baseLinePoints }: Props) => {
  const { t } = useTranslation();

  const sortedHeatLinePoints = useMemo(
    () => [...heatLinePoints].sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime()),
    [heatLinePoints]
  );
  const sortedBaseLinePoints = useMemo(
    () => [...baseLinePoints].sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime()),
    [baseLinePoints]
  );

  const minValue = useMemo(() => Math.min(...sortedHeatLinePoints.map((p) => p.y)), [sortedHeatLinePoints]);
  const maxValue = useMemo(() => Math.max(...sortedHeatLinePoints.map((p) => p.y)), [sortedHeatLinePoints]);

  const normalizeValue = (value) => {
    if (maxValue === minValue) return 0.5;
    return (value - minValue) / (maxValue - minValue);
  };

  const gradientScaleRange = 0.25;

  const dataSets: GraphicalLineChartDataset[] = useMemo(() => {
    if (sortedHeatLinePoints.length === 0 || sortedBaseLinePoints.length === 0) {
      return [];
    }

    return [
      {
        label: label,
        color: '',
        points: sortedHeatLinePoints,
        drawMode: 'gradient',
        strokeWidth: 3,
        legendItem: {
          label: '',
          icon: (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <Typography className='Legend-item-label' component='div' variant='label'>
                {t('14DayMovingAverage') + ': ' + t('High')}
              </Typography>
              <div
                style={{ display: 'flex', height: 'fit-content', borderRadius: '999px', border: '1px solid #e0e0e0', overflow: 'hidden' }}
              >
                {orderedColorSets.toReversed().map((colorSet, i) => (
                  <span key={i} style={{ backgroundColor: colorSet.bg, width: '10px', height: '8px' }}></span>
                ))}
              </div>
              <Typography className='Legend-item-label' component='div' variant='label'>
                {t('Low')}
              </Typography>
            </div>
          ),
        },
        getGradientStops: (point, nextPoint) => {
          const stops = [] as { offset: number; color: string }[];
          const totalSteps = 7;

          const lineSlope = (nextPoint.y - point.y) / (new Date(nextPoint.x).getTime() - new Date(point.x).getTime());
          const lineIntercept = point.y - lineSlope * new Date(point.x).getTime();

          const baseLineValue = sortedBaseLinePoints.find((p) => p.x === point.x)?.y ?? 0;
          const nextBaseLineValue = sortedBaseLinePoints.find((p) => p.x === nextPoint.x)?.y ?? 0;
          const baseLineSlope = (nextBaseLineValue - baseLineValue) / (new Date(nextPoint.x).getTime() - new Date(point.x).getTime());
          const baseLineIntercept = baseLineValue - baseLineSlope * new Date(point.x).getTime();

          for (let i = 0; i <= totalSteps; i++) {
            const progress = i / totalSteps;
            const currentTime = new Date(point.x).getTime() + progress * (new Date(nextPoint.x).getTime() - new Date(point.x).getTime());
            const currentY = lineSlope * currentTime + lineIntercept;
            const currentBaseLineValue = baseLineSlope * currentTime + baseLineIntercept;

            const normalizedY = normalizeValue(currentY);
            const normalizedBaseLineValue = normalizeValue(currentBaseLineValue);
            const lowerBound = normalizedBaseLineValue - gradientScaleRange;
            const upperBound = normalizedBaseLineValue + gradientScaleRange;

            const color = getOrderedColorSet(lowerBound, upperBound, normalizedY).bg;
            stops.push({ offset: progress * 100, color: color });
          }

          return stops;
        },
      },
      {
        label: t('RollingAverage'),
        color: '#414141',
        points: sortedBaseLinePoints,
        drawMode: 'groupColor',
        legendItem: {
          label: t('RollingAverage'),
          icon: <div style={{ width: '14px', border: '2px solid #414141' }}></div>,
        },
      },
    ];
  }, [sortedHeatLinePoints, sortedBaseLinePoints]);

  return <LineChart xLabel={''} yLabel={label} chartData={dataSets} dataPointVisibility='hovered' />;
};

export default HeatLineChart;
